import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { salesAPI, finishedGoodAPI, warehouseAPI, ladingAPI, accountingAPI, prepaymentAPI } from '../utils/api';
import PrepaymentPicker from '../components/common/PrepaymentPicker';
import MoneyModal from '../components/common/MoneyModal';
import { LedgerModal } from './Accounting';
import { ShoppingCart, Plus, Edit2, Trash2, X, Users, Tag, FileText, ChevronDown, ChevronUp, Wallet, CheckCircle2, RotateCcw, Search, AlertTriangle, Truck, HandCoins, BookOpen } from 'lucide-react';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';
import { PAYMENT_TYPES, localName, fmt, money, apiError, today } from '../utils/helpers';
import SectionHero from '../components/common/SectionHero';
import useUrlQuery from '../utils/useUrlQuery';

const emptyItem = () => ({ finished_good_id: '', warehouse_id: '', quantity_rolls: '', quantity_sqm: '', price_per_roll: '' });
const emptySale = () => ({
  sale_number: '', sale_date: today(), customer_id: '', notes: '', seller_name: '',
  payment_type: 'cash', due_date: '', barter_details: '', has_customs: false, lading_bill_id: null, account_id: '',
  prepayment_id: '', prepayment_amount: '',
  items: [emptyItem()],
});
const PAY_BADGE = { cash: 'badge-green', transfer: 'badge-blue', transfer_iran: 'badge-blue', credit: 'badge-yellow', barter: 'badge-orange', service: 'badge-orange' };

export default function Sales() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user, can } = useAuthStore();
  const canEdit = can('sales.edit');
  const isAdmin = can('records.delete');

  const [tab, setTab] = useState(params.get('tab') || 'sales');
  const [sales, setSales] = useState([]);
  const [debts, setDebts] = useState({ total_debt: 0, total_overdue: 0, customers: [], sales: [] });
  const [customers, setCustomers] = useState([]);
  const [prices, setPrices] = useState([]);
  const [goods, setGoods] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [receiveFor, setReceiveFor] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');
  useUrlQuery(v => { setSearch(v); setTab('sales'); setPayFilter(''); });
  const [payFilter, setPayFilter] = useState('');

  const [saleModal, setSaleModal] = useState(false);
  const [editSale, setEditSale] = useState(null);
  const [customerModal, setCustomerModal] = useState(false);
  const [priceModal, setPriceModal] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saleForm, setSaleForm] = useState(emptySale());
  const [custForm, setCustForm] = useState({ name: '', phone: '', address: '', notes: '' });
  const [priceForm, setPriceForm] = useState({ finished_good_id: '', price_per_roll: '', effective_date: today(), notes: '' });

  const load = () => {
    setLoading(true);
    accountingAPI.accounts().then(r => setAccounts(r.data)).catch(() => {});
    return Promise.all([salesAPI.list(), salesAPI.debts(), salesAPI.listCustomers(), salesAPI.listPrices(), finishedGoodAPI.list(), warehouseAPI.list()])
      .then(([s, d, c, p, g, w]) => { setSales(s.data); setDebts(d.data); setCustomers(c.data); setPrices(p.data); setGoods(g.data); setWarehouses(w.data); })
      .catch(() => toast.error(t('error')))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  // Convert a lading bill into a sale: /sales?from_bill=<id>
  useEffect(() => {
    const billId = params.get('from_bill');
    if (!billId || goods.length === 0) return;
    ladingAPI.get(billId).then(async r => {
      const b = r.data;
      if (b.sale_id) { toast.error(t('bill_already_linked')); return; }
      const num = (await salesAPI.nextNumber()).data.sale_number;
      setEditSale(null);
      setSaleForm({
        ...emptySale(), sale_number: num, sale_date: b.bill_date, customer_id: b.customer_id || '', has_customs: b.has_customs,
        lading_bill_id: b.id, notes: b.original_bill_number ? `${t('lading_bill')}: ${b.original_bill_number}` : '',
        items: b.items.map(i => ({ finished_good_id: i.finished_good_id, warehouse_id: i.warehouse_id,
          quantity_rolls: i.original_quantity_rolls, quantity_sqm: i.original_quantity_sqm, price_per_roll: priceOf(i.finished_good_id) })),
      });
      setTab('sales'); setSaleModal(true);
    }).catch(() => toast.error(t('error')));
    params.delete('from_bill'); setParams(params, { replace: true });
  }, [params, goods]); // eslint-disable-line

  // v3.9: /sales?from_prepayment=<id> - deliver goods of a customer's pre-sale contract
  useEffect(() => {
    const pid = params.get('from_prepayment');
    if (!pid || goods.length === 0) return;
    params.delete('from_prepayment'); setParams(params, { replace: true });
    Promise.all([prepaymentAPI.get(pid), salesAPI.nextNumber().catch(() => ({ data: {} }))]).then(([r, n]) => {
      const pp = r.data;
      const lines = pp.lines.filter(l => l.item_type === 'product' && l.remaining > 0);
      const usdPrice = l => (l.unit_price == null ? priceOf(l.finished_good_id) : pp.currency_code === 'USD' ? l.unit_price : '');
      setEditSale(null);
      setSaleForm({ ...emptySale(), sale_number: n.data.sale_number || '', customer_id: pp.party_id, payment_type: 'credit', due_date: today(),
        seller_name: user?.full_name || '', notes: pp.number, prepayment_id: pp.id,
        items: lines.length ? lines.map(l => ({ ...emptyItem(), finished_good_id: l.finished_good_id, quantity_rolls: l.remaining, price_per_roll: usdPrice(l) })) : [emptyItem()] });
      setTab('sales'); setSaleModal(true);
    }).catch(() => toast.error(t('error')));
  }, [params, goods]); // eslint-disable-line

  const priceOf = id => goods.find(g => g.id === id)?.current_price ?? '';
  const stockOf = (gid, wid) => goods.find(g => g.id === gid)?.stocks.find(s => s.warehouse_id === wid)?.quantity_rolls;
  const goodName = id => localName(goods.find(g => g.id === id), lang);

  const toggleExpand = id => setExpanded(e => ({ ...e, [id]: !e[id] }));
  const updateItem = (i, field, val) => setSaleForm(f => {
    const items = [...f.items];
    items[i] = { ...items[i], [field]: val };
    if (field === 'finished_good_id' && !items[i].price_per_roll) items[i].price_per_roll = priceOf(val);
    return { ...f, items };
  });
  const addItem = () => setSaleForm(f => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = i => setSaleForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const saleTotal = saleForm.items.reduce((s, i) => s + (parseFloat(i.quantity_rolls) || 0) * (parseFloat(i.price_per_roll) || 0), 0);

  const openNewSale = async () => {
    setEditSale(null);
    const f = emptySale();
    try { f.sale_number = (await salesAPI.nextNumber()).data.sale_number; } catch { /* ignore */ }
    f.seller_name = user?.full_name || '';
    setSaleForm(f); setSaleModal(true);
  };

  const openEditSale = s => {
    setEditSale(s);
    setSaleForm({ ...emptySale(), sale_number: s.sale_number, sale_date: s.sale_date, customer_id: s.customer_id || '', notes: s.notes || '',
      seller_name: s.seller_name || '', payment_type: s.payment_type, due_date: s.due_date || '', barter_details: s.barter_details || '',
      has_customs: s.has_customs, items: [] });
    setSaleModal(true);
  };

  const handleSaveSale = async () => {
    if (!saleForm.sale_number.trim()) return toast.error(t('sale_number_required'));
    if (saleForm.payment_type === 'credit' && !saleForm.due_date) return toast.error(t('due_date_required'));
    const header = {
      sale_number: saleForm.sale_number, sale_date: saleForm.sale_date, customer_id: saleForm.customer_id || null, notes: saleForm.notes,
      seller_name: saleForm.seller_name, payment_type: saleForm.payment_type, due_date: saleForm.due_date || null,
      barter_details: saleForm.barter_details, has_customs: saleForm.has_customs,
    };
    setSaving(true);
    try {
      if (editSale) {
        await salesAPI.update(editSale.id, { ...header, customer_id: saleForm.customer_id || '' });
      } else {
        if (saleForm.items.some(i => !i.finished_good_id || (!saleForm.lading_bill_id && !i.warehouse_id) || !i.quantity_rolls || i.price_per_roll === '')) {
          setSaving(false); return toast.error(t('fill_all_items'));
        }
        await salesAPI.create({ ...header, lading_bill_id: saleForm.lading_bill_id,
          account_id: saleForm.payment_type !== 'credit' && saleForm.account_id ? saleForm.account_id : null,
          prepayment_id: saleForm.payment_type === 'credit' && saleForm.prepayment_id ? saleForm.prepayment_id : null,
          prepayment_amount: saleForm.prepayment_amount ? parseFloat(saleForm.prepayment_amount) : null,
          items: saleForm.items.map(i => ({ finished_good_id: i.finished_good_id, warehouse_id: i.warehouse_id || null,
            quantity_rolls: parseFloat(i.quantity_rolls), quantity_sqm: parseFloat(i.quantity_sqm) || 0, price_per_roll: parseFloat(i.price_per_roll) })) });
      }
      toast.success(t('saved')); setSaleModal(false); setSaleForm(emptySale()); load();
    } catch (e) { toast.error(apiError(e, t)); } finally { setSaving(false); }
  };

  const handleSaveCustomer = async () => {
    if (!custForm.name.trim()) return toast.error(t('name_required'));
    setSaving(true);
    try {
      if (editCustomer) await salesAPI.updateCustomer(editCustomer.id, custForm);
      else await salesAPI.createCustomer(custForm);
      toast.success(t('saved'));
      setCustomerModal(false); setEditCustomer(null); setCustForm({ name: '', phone: '', address: '', notes: '' }); load();
    } catch (e) { toast.error(apiError(e, t)); } finally { setSaving(false); }
  };

  const handleSavePrice = async () => {
    if (!priceForm.finished_good_id || !priceForm.price_per_roll) return toast.error(t('fill_required'));
    setSaving(true);
    try {
      await salesAPI.setPrice({ ...priceForm, price_per_roll: parseFloat(priceForm.price_per_roll) });
      toast.success(t('saved')); setPriceModal(false); setPriceForm({ finished_good_id: '', price_per_roll: '', effective_date: today(), notes: '' }); load();
    } catch (e) { toast.error(apiError(e, t)); } finally { setSaving(false); }
  };

  const confirmThen = (fn, msg = t('confirm_delete')) => async (...a) => {
    if (!window.confirm(msg)) return;
    try { await fn(...a); toast.success(t('saved')); load(); } catch (e) { toast.error(apiError(e, t)); }
  };
  const handleDeleteSale = confirmThen(id => salesAPI.delete(id));
  const handleDeleteCustomer = confirmThen(id => salesAPI.deleteCustomer(id));
  const handleDeletePrice = confirmThen(id => salesAPI.deletePrice(id));
  const handleMarkPaid = confirmThen(id => salesAPI.markPaid(id), t('confirm_mark_paid'));
  const handleMarkUnpaid = confirmThen(id => salesAPI.markUnpaid(id), t('confirm_mark_unpaid'));

  const q = search.trim().toLowerCase();
  const filteredSales = useMemo(() => sales.filter(s =>
    (!payFilter || s.payment_type === payFilter) &&
    (!q || [s.sale_number, s.customer, s.seller_name, s.notes].some(v => (v || '').toLowerCase().includes(q)))
  ), [sales, payFilter, q]);

  const statusBadge = s => {
    if (s.payment_type !== 'credit') return <span className="badge badge-green">{t('paid')}</span>;
    if (s.payment_status === 'paid') return <span className="badge badge-green"><CheckCircle2 size={10} /> {t('paid')}</span>;
    if (s.is_overdue) return <span className="badge badge-red"><AlertTriangle size={10} /> {t('overdue')}</span>;
    return <span className="badge badge-yellow">{t('unpaid')}</span>;
  };

  const SaleRows = ({ list, showPayAction }) => list.map(s => (
    <React.Fragment key={s.id}>
      <tr style={{ cursor: 'pointer' }} onClick={() => toggleExpand(s.id)}>
        <td>{expanded[s.id] ? <ChevronUp size={14} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />}</td>
        <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
          {s.sale_number}
          {s.lading_bill_id && <Truck size={11} style={{ marginInlineStart: 6, color: 'var(--accent)' }} title={t('linked_to_bill')} />}
        </td>
        <td>{s.sale_date}</td>
        <td>{s.customer || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
        <td className="hide-mobile"><span className={`badge ${PAY_BADGE[s.payment_type] || ''}`}>{t('pay_' + s.payment_type)}</span></td>
        <td>{statusBadge(s)}{s.payment_type === 'credit' && s.payment_status !== 'paid' && <div className="text-xs" style={{ color: '#fbbf24' }}>{t('remaining')}: {money(s.remaining)}</div>}{s.payment_type === 'credit' && s.due_date && s.payment_status !== 'paid' && <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('due_date')}: {s.due_date}</div>}</td>
        <td style={{ color: 'var(--green)', fontWeight: 700 }}>{money(s.total_amount)}</td>
        {canEdit && (
          <td onClick={e => e.stopPropagation()}>
            <div className="flex gap-1">
              {s.payment_type === 'credit' && s.payment_status !== 'paid' && (
                <button className="btn btn-sm" style={{ background: 'var(--green-dim)', color: '#34d399', border: '1px solid rgba(16,185,129,0.25)' }} onClick={() => setReceiveFor(s)}>
                  <HandCoins size={13} /> {t('receive_payment')}
                </button>
              )}
              {s.payment_type === 'credit' && s.payment_status !== 'paid' && (
                <button className="btn btn-ghost btn-sm btn-icon" title={t('mark_paid')} onClick={() => handleMarkPaid(s.id)}><CheckCircle2 size={13} /></button>
              )}
              {showPayAction && s.payment_type === 'credit' && s.payment_status === 'paid' && (
                <button className="btn btn-ghost btn-sm btn-icon" title={t('mark_unpaid')} onClick={() => handleMarkUnpaid(s.id)}><RotateCcw size={13} /></button>
              )}
              {!s.lading_bill_id && <button className="btn btn-ghost btn-sm btn-icon" title={t('issue_bill')} onClick={() => navigate(`/lading-bills?from_sale=${s.id}`)}><Truck size={13} /></button>}
              <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openEditSale(s)}><Edit2 size={13} /></button>
              {isAdmin && <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDeleteSale(s.id)}><Trash2 size={13} /></button>}
            </div>
          </td>
        )}
      </tr>
      {expanded[s.id] && (
        <tr><td colSpan={canEdit ? 8 : 7} style={{ background: 'rgba(0,0,0,0.2)', padding: '12px 20px' }}>
          <div className="flex flex-wrap gap-3 text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>
            {s.seller_name && <span>{t('seller')}: <strong>{s.seller_name}</strong></span>}
            <span>{t('payment_type')}: <strong>{t('pay_' + s.payment_type)}</strong></span>
            {s.has_customs && <span className="badge badge-orange">{t('has_customs')}</span>}
            {s.paid_at && s.payment_type === 'credit' && <span>{t('paid_at')}: {String(s.paid_at).slice(0, 10)}</span>}
            {s.received_usd > 0 && <span>{t('received')}: <strong style={{ color: '#34d399' }}>{money(s.received_usd)}</strong></span>}
          </div>
          {s.barter_details && <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>{t('barter_details')}: {s.barter_details}</p>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {s.items.map((it, i) => (
              <div key={i} className="flex items-center justify-between text-xs" style={{ background: 'var(--bg-card)', padding: '6px 10px', borderRadius: 8, gap: 8, flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--text-primary)' }}>{localName(it, lang)} {it.warehouse_name && <span style={{ color: 'var(--text-muted)' }}>· {it.warehouse_name}</span>}</span>
                <span style={{ color: 'var(--text-secondary)' }}>{fmt(it.quantity_rolls)} {t('rolls')} ({fmt(it.quantity_sqm)} {t('sqm')}) × {money(it.price_per_roll)} = <strong style={{ color: 'var(--green)' }}>{money(it.total_price)}</strong></span>
              </div>
            ))}
          </div>
          {s.notes && <p className="text-xs mt-2" style={{ color: 'var(--text-muted)', whiteSpace: 'pre-wrap' }}>{s.notes}</p>}
        </td></tr>
      )}
    </React.Fragment>
  ));

  const SalesTable = ({ list, showPayAction }) => (
    <div className="table-wrap"><table>
      <thead><tr>
        <th style={{ width: 32 }}></th><th>{t('sale_number')}</th><th>{t('date')}</th><th>{t('customer')}</th>
        <th className="hide-mobile">{t('payment_type')}</th><th>{t('status')}</th><th>{t('amount')}</th>
        {canEdit && <th>{t('actions')}</th>}
      </tr></thead>
      <tbody><SaleRows list={list} showPayAction={showPayAction} /></tbody>
    </table></div>
  );

  return (
    <div className="space-y-5">
      <SectionHero title={t('sales')} subtitle={<>{sales.length} {t('transactions')} · {customers.length} {t('customers')}</>}>
        {canEdit && (
          <div className="flex gap-2">
            {tab === 'sales' && <button className="btn btn-primary" onClick={openNewSale}><Plus size={16} /> {t('new_sale')}</button>}
            {tab === 'customers' && <button className="btn btn-primary" onClick={() => { setEditCustomer(null); setCustForm({ name: '', phone: '', address: '', notes: '' }); setCustomerModal(true); }}><Plus size={16} /> {t('add_customer')}</button>}
            {tab === 'prices' && <button className="btn btn-primary" onClick={() => setPriceModal(true)}><Plus size={16} /> {t('set_price')}</button>}
          </div>
        )}
      </SectionHero>

      <div className="tabs">
        <button className={`tab ${tab === 'sales' ? 'active' : ''}`} onClick={() => setTab('sales')}><FileText size={14} /> {t('sales')}</button>
        <button className={`tab ${tab === 'debts' ? 'active' : ''}`} onClick={() => setTab('debts')}>
          <Wallet size={14} /> {t('customer_debts')}
          {debts.sales.length > 0 && <span className="badge badge-red" style={{ padding: '1px 7px' }}>{debts.sales.length}</span>}
        </button>
        <button className={`tab ${tab === 'customers' ? 'active' : ''}`} onClick={() => setTab('customers')}><Users size={14} /> {t('customers')}</button>
        <button className={`tab ${tab === 'prices' ? 'active' : ''}`} onClick={() => setTab('prices')}><Tag size={14} /> {t('prices')}</button>
      </div>

      {loading ? <div className="loading-center"><div className="spinner" /></div> : (
        <>
          {tab === 'sales' && (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '1 1 200px' }}>
                  <Search size={15} style={{ color: 'var(--text-muted)' }} />
                  <input className="input" style={{ border: 'none', background: 'transparent', padding: 0 }} placeholder={t('search') + '...'} value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <select className="select" style={{ width: 190 }} value={payFilter} onChange={e => setPayFilter(e.target.value)}>
                  <option value="">{t('all_payment_types')}</option>
                  {PAYMENT_TYPES.map(p => <option key={p} value={p}>{t('pay_' + p)}</option>)}
                </select>
              </div>
              {filteredSales.length === 0
                ? <div className="empty-state"><ShoppingCart size={32} style={{ color: 'var(--text-muted)', margin: '0 auto 8px' }} /><p>{t('no_data')}</p></div>
                : <SalesTable list={filteredSales} showPayAction />}
            </div>
          )}

          {tab === 'debts' && (
            <div className="space-y-5">
              <div className="grid-3">
                <div className="stat-card"><div><p className="stat-label">{t('total_debt')}</p><p className="stat-value" style={{ color: '#fbbf24' }}>{money(debts.total_debt)}</p></div>
                  <div className="stat-icon" style={{ background: 'var(--yellow-dim)' }}><Wallet size={20} style={{ color: 'var(--yellow)' }} /></div></div>
                <div className="stat-card"><div><p className="stat-label">{t('overdue')}</p><p className="stat-value" style={{ color: '#f87171' }}>{money(debts.total_overdue)}</p></div>
                  <div className="stat-icon" style={{ background: 'var(--red-dim)' }}><AlertTriangle size={20} style={{ color: 'var(--red)' }} /></div></div>
                <div className="stat-card"><div><p className="stat-label">{t('debtor_customers')}</p><p className="stat-value">{debts.customers.length}</p></div>
                  <div className="stat-icon" style={{ background: 'var(--accent-dim)' }}><Users size={20} style={{ color: 'var(--accent)' }} /></div></div>
              </div>
              {debts.customers.length > 0 && (
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div className="card-header" style={{ padding: '14px 20px', margin: 0 }}><span className="card-title">{t('debt_by_customer')}</span></div>
                  <div className="table-wrap"><table>
                    <thead><tr><th>{t('customer')}</th><th>{t('invoices')}</th><th>{t('overdue')}</th><th>{t('total_debt')}</th></tr></thead>
                    <tbody>{debts.customers.map((c, i) => (
                      <tr key={i}>
                        <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{c.customer || t('no_customer')}</td>
                        <td>{c.count}</td>
                        <td style={{ color: c.overdue_amount > 0 ? '#f87171' : 'var(--text-muted)' }}>{money(c.overdue_amount)}</td>
                        <td style={{ color: '#fbbf24', fontWeight: 700 }}>{money(c.total_debt)}</td>
                      </tr>
                    ))}</tbody>
                  </table></div>
                </div>
              )}
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="card-header" style={{ padding: '14px 20px', margin: 0 }}><span className="card-title">{t('unpaid_invoices')}</span></div>
                {debts.sales.length === 0
                  ? <div className="empty-state"><CheckCircle2 size={32} style={{ color: 'var(--green)', margin: '0 auto 8px' }} /><p>{t('no_debts')}</p></div>
                  : <SalesTable list={debts.sales} />}
              </div>
            </div>
          )}

          {tab === 'customers' && (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {customers.length === 0 ? <div className="empty-state"><Users size={32} style={{ color: 'var(--text-muted)', margin: '0 auto 8px' }} /><p>{t('no_data')}</p></div> : (
                <div className="table-wrap"><table>
                  <thead><tr><th>{t('name')}</th><th>{t('phone')}</th><th className="hide-mobile">{t('address')}</th><th>{t('total_sales')}</th><th>{t('debt')}</th>{canEdit && <th>{t('actions')}</th>}</tr></thead>
                  <tbody>{customers.map(c => (
                    <tr key={c.id}>
                      <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{c.name}</td>
                      <td dir="ltr">{c.phone || '—'}</td>
                      <td className="hide-mobile">{c.address || '—'}</td>
                      <td style={{ color: 'var(--green)' }}>{money(c.total_sales)}</td>
                      <td style={{ color: c.debt > 0 ? '#fbbf24' : 'var(--text-muted)', fontWeight: c.debt > 0 ? 700 : 400 }}>{money(c.debt)}</td>
                      {canEdit && <td><div className="flex gap-1">
                        <button className="btn btn-ghost btn-sm" onClick={() => accountingAPI.ledger({ customer_id: c.id }).then(r => setLedger(r.data)).catch(() => toast.error(t('error')))}><BookOpen size={13} /> {t('ledger')}</button>
                        <button className="btn btn-ghost btn-sm btn-icon" onClick={() => { setEditCustomer(c); setCustForm({ name: c.name, phone: c.phone || '', address: c.address || '', notes: c.notes || '' }); setCustomerModal(true); }}><Edit2 size={13} /></button>
                        <button className="btn btn-ghost btn-sm btn-icon" style={{ color: '#f87171' }} onClick={() => handleDeleteCustomer(c.id)}><Trash2 size={13} /></button>
                      </div></td>}
                    </tr>
                  ))}</tbody>
                </table></div>
              )}
            </div>
          )}

          {tab === 'prices' && (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {prices.length === 0 ? <div className="empty-state"><Tag size={32} style={{ color: 'var(--text-muted)', margin: '0 auto 8px' }} /><p>{t('no_data')}</p></div> : (
                <div className="table-wrap"><table>
                  <thead><tr><th>{t('product')}</th><th>{t('price_per_roll')}</th><th>{t('effective_date')}</th><th className="hide-mobile">{t('notes')}</th>{canEdit && <th>{t('actions')}</th>}</tr></thead>
                  <tbody>{prices.map(p => (
                    <tr key={p.id}>
                      <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{localName(p, lang)}</td>
                      <td style={{ color: 'var(--green)', fontWeight: 700 }}>{money(p.price_per_roll)}</td>
                      <td>{p.effective_date}</td>
                      <td className="hide-mobile" style={{ color: 'var(--text-muted)' }}>{p.notes || '—'}</td>
                      {canEdit && <td><button className="btn btn-ghost btn-sm btn-icon" style={{ color: '#f87171' }} onClick={() => handleDeletePrice(p.id)}><Trash2 size={13} /></button></td>}
                    </tr>
                  ))}</tbody>
                </table></div>
              )}
            </div>
          )}
        </>
      )}

      {saleModal && (
        <div className="modal-overlay" onClick={() => setSaleModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="card-title">{editSale ? t('edit_sale') : saleForm.lading_bill_id ? t('convert_bill_to_sale') : t('new_sale')}</h2>
              <button className="btn btn-ghost btn-icon" onClick={() => setSaleModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {saleForm.lading_bill_id && <div className="alert alert-success mb-3"><Truck size={15} /> <span>{t('bill_stock_note')}</span></div>}
              <div className="form-row">
                <div className="form-group"><label className="form-label">{t('sale_number')} *</label><input className="input" value={saleForm.sale_number} onChange={e => setSaleForm(f => ({ ...f, sale_number: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">{t('date')}</label><input className="input" type="date" value={saleForm.sale_date} onChange={e => setSaleForm(f => ({ ...f, sale_date: e.target.value }))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">{t('customer')}</label>
                  <select className="select" value={saleForm.customer_id} onChange={e => setSaleForm(f => ({ ...f, customer_id: e.target.value }))}>
                    <option value="">{t('no_customer')}</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group"><label className="form-label">{t('seller')}</label><input className="input" value={saleForm.seller_name} onChange={e => setSaleForm(f => ({ ...f, seller_name: e.target.value }))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">{t('payment_type')}</label>
                  <select className="select" value={saleForm.payment_type} onChange={e => setSaleForm(f => ({ ...f, payment_type: e.target.value }))}>
                    {PAYMENT_TYPES.map(p => <option key={p} value={p}>{t('pay_' + p)}</option>)}
                  </select>
                </div>
                {!editSale && saleForm.payment_type !== 'credit' && accounts.length > 0 && (
                  <div className="form-group"><label className="form-label">{t('deposit_account')}</label>
                    <select className="select" value={saleForm.account_id} onChange={e => setSaleForm(f => ({ ...f, account_id: e.target.value }))}>
                      <option value="">{t('no_account_record')}</option>
                      {accounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.currency_code})</option>)}
                    </select></div>
                )}
                {saleForm.payment_type === 'credit' && (
                  <div className="form-group"><label className="form-label">{t('due_date')} *</label><input className="input" type="date" value={saleForm.due_date} onChange={e => setSaleForm(f => ({ ...f, due_date: e.target.value }))} /></div>
                )}
              </div>
              {!editSale && <PrepaymentPicker kind="customer" partyId={saleForm.customer_id} value={{ prepayment_id: saleForm.prepayment_id, prepayment_amount: saleForm.prepayment_amount }}
                onChange={v => setSaleForm(f => ({ ...f, ...v }))} onNeedCredit={() => setSaleForm(f => ({ ...f, payment_type: 'credit', due_date: f.due_date || today() }))} />}
              {saleForm.payment_type === 'barter' && (
                <div className="form-group"><label className="form-label">{t('barter_details')}</label>
                  <textarea className="input" value={saleForm.barter_details} onChange={e => setSaleForm(f => ({ ...f, barter_details: e.target.value }))} style={{ minHeight: 56 }} /></div>
              )}
              <div className="checkbox-wrap mb-3" onClick={() => setSaleForm(f => ({ ...f, has_customs: !f.has_customs }))}>
                <input type="checkbox" checked={saleForm.has_customs} readOnly />
                <label style={{ cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)' }}>{t('has_customs')}</label>
              </div>

              {!editSale && (
                <>
                  <div className="divider" />
                  <div className="flex items-center justify-between mb-3">
                    <p className="form-label" style={{ margin: 0 }}>{t('items')}</p>
                    {!saleForm.lading_bill_id && <button className="btn btn-secondary btn-sm" onClick={addItem}><Plus size={13} /> {t('add')}</button>}
                  </div>
                  {saleForm.items.map((item, i) => {
                    const avail = stockOf(item.finished_good_id, item.warehouse_id);
                    const over = !saleForm.lading_bill_id && avail != null && parseFloat(item.quantity_rolls) > avail;
                    return (
                      <div key={i} style={{ padding: 12, borderRadius: 12, background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', marginBottom: 8 }}>
                        <div className="flex gap-2 flex-wrap items-center mb-2">
                          <select className="select" disabled={!!saleForm.lading_bill_id} value={item.finished_good_id} onChange={e => updateItem(i, 'finished_good_id', e.target.value)} style={{ flex: '2 1 160px' }}>
                            <option value="">{t('select_product')}</option>
                            {goods.map(g => <option key={g.id} value={g.id}>{localName(g, lang)}</option>)}
                          </select>
                          <select className="select" disabled={!!saleForm.lading_bill_id} value={item.warehouse_id} onChange={e => updateItem(i, 'warehouse_id', e.target.value)} style={{ flex: '2 1 140px' }}>
                            <option value="">{t('select_warehouse')}</option>
                            {warehouses.map(w => {
                              const st = stockOf(item.finished_good_id, w.id);
                              return <option key={w.id} value={w.id}>{w.name}{item.finished_good_id && st != null ? ` (${fmt(st)} ${t('rolls')})` : ''}</option>;
                            })}
                          </select>
                          {saleForm.items.length > 1 && !saleForm.lading_bill_id && <button className="btn btn-ghost btn-icon" onClick={() => removeItem(i)}><X size={14} /></button>}
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <input className="input" type="number" min="0" step="any" disabled={!!saleForm.lading_bill_id} placeholder={t('rolls')} value={item.quantity_rolls} onChange={e => updateItem(i, 'quantity_rolls', e.target.value)} style={{ flex: '1 1 80px', borderColor: over ? 'var(--red)' : undefined }} />
                          <input className="input" type="number" min="0" step="any" disabled={!!saleForm.lading_bill_id} placeholder={t('sqm')} value={item.quantity_sqm} onChange={e => updateItem(i, 'quantity_sqm', e.target.value)} style={{ flex: '1 1 80px' }} />
                          <input className="input" type="number" min="0" step="any" placeholder={t('price_per_roll')} value={item.price_per_roll} onChange={e => updateItem(i, 'price_per_roll', e.target.value)} style={{ flex: '1 1 100px' }} />
                        </div>
                        {item.finished_good_id && item.warehouse_id && !saleForm.lading_bill_id && (
                          <p className="text-xs mt-1" style={{ color: over ? 'var(--red)' : 'var(--text-muted)' }}>{goodName(item.finished_good_id)} · {t('available')}: {fmt(avail || 0)} {t('rolls')}</p>
                        )}
                      </div>
                    );
                  })}
                  {saleTotal > 0 && <div className="flex justify-end mt-2"><span className="badge badge-green" style={{ fontSize: 13, padding: '5px 14px' }}>{t('total')}: {money(saleTotal)}</span></div>}
                </>
              )}
              <div className="divider" />
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{t('notes')}</label>
                <textarea className="input" value={saleForm.notes} onChange={e => setSaleForm(f => ({ ...f, notes: e.target.value }))} style={{ minHeight: 56 }} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setSaleModal(false)}>{t('cancel')}</button>
              <button className="btn btn-primary" onClick={handleSaveSale} disabled={saving}>{saving ? t('loading') : t('save')}</button>
            </div>
          </div>
        </div>
      )}

      {receiveFor && (
        <MoneyModal title={`${t('receive_payment')} — ${receiveFor.sale_number}`} suggestedUsd={receiveFor.remaining} submitLabel={t('receive_payment')}
          onSubmit={payload => salesAPI.receive(receiveFor.id, payload)} onClose={changed => { setReceiveFor(null); if (changed) load(); }} />
      )}
      {ledger && <LedgerModal t={t} ledger={ledger} onClose={() => setLedger(null)} />}

      {customerModal && (
        <div className="modal-overlay" onClick={() => setCustomerModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2 className="card-title">{editCustomer ? t('edit_customer') : t('add_customer')}</h2><button className="btn btn-ghost btn-icon" onClick={() => setCustomerModal(false)}><X size={18} /></button></div>
            <div className="modal-body">
              <div className="form-group"><label className="form-label">{t('name')} *</label><input className="input" value={custForm.name} onChange={e => setCustForm(f => ({ ...f, name: e.target.value }))} /></div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">{t('phone')}</label><input className="input" dir="ltr" value={custForm.phone} onChange={e => setCustForm(f => ({ ...f, phone: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">{t('address')}</label><input className="input" value={custForm.address} onChange={e => setCustForm(f => ({ ...f, address: e.target.value }))} /></div>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">{t('notes')}</label><textarea className="input" value={custForm.notes} onChange={e => setCustForm(f => ({ ...f, notes: e.target.value }))} style={{ minHeight: 56 }} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setCustomerModal(false)}>{t('cancel')}</button>
              <button className="btn btn-primary" onClick={handleSaveCustomer} disabled={saving}>{saving ? t('loading') : t('save')}</button>
            </div>
          </div>
        </div>
      )}

      {priceModal && (
        <div className="modal-overlay" onClick={() => setPriceModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2 className="card-title">{t('set_price')}</h2><button className="btn btn-ghost btn-icon" onClick={() => setPriceModal(false)}><X size={18} /></button></div>
            <div className="modal-body">
              <div className="form-group"><label className="form-label">{t('product')} *</label>
                <select className="select" value={priceForm.finished_good_id} onChange={e => setPriceForm(f => ({ ...f, finished_good_id: e.target.value }))}>
                  <option value="">{t('select_product')}</option>
                  {goods.map(g => <option key={g.id} value={g.id}>{localName(g, lang)}</option>)}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">{t('price_per_roll')} *</label><input className="input" type="number" min="0" step="any" value={priceForm.price_per_roll} onChange={e => setPriceForm(f => ({ ...f, price_per_roll: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">{t('effective_date')}</label><input className="input" type="date" value={priceForm.effective_date} onChange={e => setPriceForm(f => ({ ...f, effective_date: e.target.value }))} /></div>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">{t('notes')}</label><input className="input" value={priceForm.notes} onChange={e => setPriceForm(f => ({ ...f, notes: e.target.value }))} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setPriceModal(false)}>{t('cancel')}</button>
              <button className="btn btn-primary" onClick={handleSavePrice} disabled={saving}>{saving ? t('loading') : t('save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
